/**
 * VH-287 — choose a password with the set-up code from the mail.
 *
 * The mail's link fills email and code (they come after `#`, see
 * `services/setupLink.js`); typed by hand works the same. The answer is the password
 * step's: a ticket for the code, or for the set-up, which the same code then
 * opens (`onDone(step, code)`).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { authAPI } from '../../services/api'
import { AUTH_ERROR, readAuthError } from '../../services/authErrors'

const PASSWORD_MIN = 12
const PASSWORD_MAX = 200

const ERRORS = {
  [AUTH_ERROR.SETUP_CODE_WRONG]: 'secondFactor.setupCode.wrong',
  [AUTH_ERROR.SETUP_CODE_USED]: 'enrol.codeUsed',
  [AUTH_ERROR.SETUP_CODE_EXPIRED]: 'enrol.codeExpired',
  [AUTH_ERROR.HELD]: 'auth.loginRefused',
  [AUTH_ERROR.NETWORK]: 'secondFactor.code.unreachable',
}

export default function EnrolForm({ initialEmail = '', initialCode = '', onDone, children }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState(initialCode)
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState(null)
  const [sending, setSending] = useState(false)

  const submit = async (event) => {
    event.preventDefault()
    if (sending) return
    if (!email.trim() || !code.trim()) {
      setError(t('enrol.fillIn'))
      return
    }
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      setError(t('enrol.passwordRule'))
      return
    }
    if (password !== repeat) {
      setError(t('enrol.passwordsDiffer'))
      return
    }
    setSending(true)
    setError(null)
    try {
      const { data } = await authAPI.enrol(email.trim(), code.trim(), password)
      onDone(data, code.trim())
    } catch (err) {
      const { kind, status } = readAuthError(err)
      const detail = err?.response?.data?.detail
      if (detail?.code === 'deactivated') setError(t('auth.accountDeactivated'))
      else if (status === 422) setError(t('enrol.passwordRule'))
      else setError(t(ERRORS[kind] || 'secondFactor.code.failed', { status }))
    } finally {
      setSending(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={submit} noValidate>
      <p className="text-sm text-gray-700">{t('enrol.intro')}</p>
      <div>
        <label htmlFor="enrol-email" className="form-label">{t('auth.email')}</label>
        <input
          id="enrol-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="form-input"
        />
      </div>
      <div>
        <label htmlFor="enrol-code" className="form-label">{t('secondFactor.setupCode.label')}</label>
        <input
          id="enrol-code"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          className="form-input font-mono tracking-wider"
        />
      </div>
      <div>
        <label htmlFor="enrol-password" className="form-label">{t('enrol.newPassword')}</label>
        <input
          id="enrol-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="form-input"
        />
        <p className="mt-1 text-xs text-gray-500">{t('enrol.passwordRule')}</p>
      </div>
      <div>
        <label htmlFor="enrol-repeat" className="form-label">{t('enrol.repeatPassword')}</label>
        <input
          id="enrol-repeat"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          className="form-input"
        />
      </div>
      {error && (
        <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        {children}
        <button type="submit" disabled={sending} className="btn-primary w-full sm:flex-1">
          {sending ? t('enrol.saving') : t('enrol.submit')}
        </button>
      </div>
    </form>
  )
}
